import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#0f172a", primaryBlue: "#1e40af", accentCyan: "#0ea5e9",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0", successDark: "#15803d",
  dangerDark: "#b91c1c", warningDark: "#a16207"
};

const StockIssueReport = () => {
  // Start with completely empty state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchReport = async () => {
    // 🔥 STRICT ENFORCEMENT: Force user to pick dates before fetching
    if (!startDate || !endDate) {
      return alert("Please select both a Start Date and End Date to generate the report.");
    }
    if (new Date(startDate) > new Date(endDate)) {
      return alert("Start date cannot be after End date.");
    }

    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/reports/medicine-stock-issue?start_date=${startDate}&end_date=${endDate}&employee_id=${user?.id}`);
      const data = await res.json();
      if (data.success) {
        setReportData(data.data);
        setHasSearched(true);
      } else {
        alert(data.message || "Failed to fetch report");
      }
    } catch (err) {
      console.error("Report Fetch Error:", err);
      alert("Network error occurred.");
    }
    setLoading(false);
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setReportData([]);
    setHasSearched(false);
  };

  // --- EXPORT TO EXCEL (CSV) ---
  const exportToExcel = () => {
    if (reportData.length === 0) return alert("No data to export!");

    const headers = [
      "Issue ID", "Issue Date", "Issued To (Location)", 
      "Medicine ID", "Medicine Name", "Unit", 
      "Batch No", "Expiry Date", "Issued Qty"
    ];

    const csvRows = reportData.map(row => [
      row.issue_id, row.issue_dt, `"${row.issued_to}"`,
      row.medicine_id, `"${row.medicine_name}"`, row.unit,
      row.batch_no, row.expiry_date, row.issued_qty
    ].join(","));

    const csvString = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Stock_Issue_Report_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- DIRECT PDF DOWNLOAD (AutoTable) ---
  const exportToPDF = () => {
    if (reportData.length === 0) return alert("No data to print!");

    const doc = new jsPDF("landscape");

    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text("EMEDHA CARE HOSPITAL - Stock Transfer / Issue Report", 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Date Range: ${startDate} to ${endDate}  |  Generated On: ${new Date().toLocaleString()}`, 14, 22);

    // Group data by Issue ID
    const groupedData = reportData.reduce((acc, row) => {
      if (!acc[row.issue_id]) {
        acc[row.issue_id] = {
          issue_id: row.issue_id,
          date: row.issue_dt,
          issued_to: row.issued_to,
          items: []
        };
      }
      acc[row.issue_id].items.push(row);
      return acc;
    }, {});

    let startY = 30;

    Object.values(groupedData).forEach((issue, index) => {
      if (startY > 160) {
        doc.addPage();
        startY = 20;
      } else if (index > 0) {
        startY += 10;
      }

      // Issue Header Info
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.setFont(undefined, 'bold');
      doc.text(`Issue No: ${issue.issue_id} | Date: ${issue.date}`, 14, startY);
      doc.setFont(undefined, 'normal');
      doc.text(`Issued To (Location/Ward): ${issue.issued_to}`, 14, startY + 5);

      const tableColumn = ["Medicine Name (ID)", "Unit", "Batch No", "Expiry Date", "Issued Qty"];
      const tableRows = [];

      issue.items.forEach(item => {
        tableRows.push([
          `${item.medicine_name}\n(${item.medicine_id})`,
          item.unit,
          item.batch_no || '-',
          item.expiry_date || '-',
          item.issued_qty
        ]);
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: startY + 8,
        styles: { fontSize: 8, cellPadding: 3, valign: 'middle' },
        headStyles: { fillColor: [226, 232, 240], textColor: [15, 23, 42], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          4: { fontStyle: 'bold', textColor: [21, 128, 61], halign: 'right' } // Highlight Issued Qty
        },
        margin: { left: 14, right: 14 }
      });

      startY = doc.lastAutoTable.finalY + 5;
    });

    doc.save(`Stock_Issue_Report_${startDate}_to_${endDate}.pdf`);
  };

  return (
    <div className="registration-container fade-in" style={{ backgroundColor: theme.bgLight, minHeight: '100vh', padding: '20px 2%', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      
      <style>{`
        .report-header-container { background: #fff; padding: 20px 25px; border-radius: 12px; border: 1px solid ${theme.borderLight}; box-shadow: 0 4px 15px rgba(0,0,0,0.03); margin-bottom: 25px; }
        .report-title { margin: 0 0 15px 0; color: ${theme.primaryDark}; font-size: 22px; font-weight: 800; }
        
        .report-toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 20px; }
        
        .filter-group { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .date-input-wrap { display: flex; align-items: center; gap: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 12px; height: 42px; box-sizing: border-box; }
        .date-input-wrap label { font-size: 12px; font-weight: 700; color: #64748b; }
        .date-input-wrap input { border: none; background: transparent; outline: none; color: #334155; font-weight: 600; font-family: inherit; font-size: 13px; }
        
        .action-group { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .btn-base { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 0 16px; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; transition: 0.2s; font-size: 13px; height: 42px; white-space: nowrap; box-sizing: border-box; }
        .btn-filter { background: ${theme.primaryBlue}; color: white; }
        .btn-filter:hover { background: #1e3a8a; box-shadow: 0 4px 10px rgba(30, 64, 175, 0.2); }
        .btn-clear { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
        .btn-clear:hover { background: #e2e8f0; color: #0f172a; }
        .btn-pdf { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .btn-pdf:hover { background: #fecaca; }
        .btn-excel { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        .btn-excel:hover { background: #bbf7d0; }

        .erp-grid { width: 100%; border-collapse: collapse; background: #fff; font-size: 13px; }
        .erp-grid th { background: #f1f5f9; color: ${theme.primaryDark}; padding: 14px 15px; text-align: left; font-weight: 700; border-bottom: 2px solid ${theme.borderLight}; white-space: nowrap; text-transform: uppercase; font-size: 11.5px; }
        .erp-grid td { padding: 10px 15px; border-bottom: 1px solid ${theme.borderLight}; color: ${theme.textMain}; white-space: nowrap; vertical-align: middle; }
        .erp-grid tr:hover { background-color: #f8fafc; }
        .table-wrapper { background: #fff; border-radius: 12px; border: 1px solid ${theme.borderLight}; overflow-x: auto; box-shadow: 0 4px 15px rgba(0,0,0,0.03); }
        
        .clean-scrollbar::-webkit-scrollbar { height: 8px; }
        .clean-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

        .sub-text { font-size: 11px; color: ${theme.textMuted}; font-weight: 500; display: block; margin-top: 2px; }
      `}</style>

      <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        
        {/* HEADER TOOLBAR */}
        <div className="report-header-container">
          <h2 className="report-title">Stock Transfer / Issue Report</h2>
          
          <div className="report-toolbar">
            
            {/* Filter Section (Left) */}
            <div className="filter-group">
              <div className="date-input-wrap">
                <label>From <span style={{color:'red'}}>*</span></label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="date-input-wrap">
                <label>To <span style={{color:'red'}}>*</span></label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <button className="btn-base btn-filter" onClick={fetchReport}>🔍 Generate Data</button>
              {hasSearched && (
                <button className="btn-base btn-clear" onClick={handleClearFilters}>✖ Clear</button>
              )}
            </div>

            {/* Actions Section (Right) */}
            <div className="action-group">
              <button className="btn-base btn-pdf" onClick={exportToPDF} disabled={loading || reportData.length === 0} style={{ opacity: reportData.length === 0 ? 0.5 : 1 }}>
                📄 Download PDF
              </button>
              <button className="btn-base btn-excel" onClick={exportToExcel} disabled={loading || reportData.length === 0} style={{ opacity: reportData.length === 0 ? 0.5 : 1 }}>
                📊 Download Excel
              </button>
            </div>

          </div>
        </div>

        {/* DATA GRID */}
        {loading ? (
          <div style={{ padding: '50px', textAlign: 'center', color: theme.accentCyan, fontWeight: '700', fontSize: '16px' }}>Fetching Records...</div>
        ) : (
          <div className="table-wrapper clean-scrollbar">
            <table className="erp-grid">
              <thead>
                <tr>
                  <th>Issue Info</th>
                  <th>Location Issued To</th>
                  <th>Medicine Info</th>
                  <th>Batch & Expiry</th>
                  <th style={{ textAlign: 'right' }}>Issued Qty</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, idx) => (
                  <tr key={idx}>
                    
                    {/* Issue Details */}
                    <td>
                      <span style={{ fontWeight: '800', color: theme.primaryBlue }}>{row.issue_id}</span>
                      <span className="sub-text">Date: {row.issue_dt}</span>
                    </td>

                    {/* Issued To */}
                    <td>
                      <span style={{ fontWeight: '700', color: theme.primaryDark }}>{row.issued_to}</span>
                    </td>

                    {/* Medicine */}
                    <td>
                      <span style={{ fontWeight: '700' }}>{row.medicine_name}</span>
                      <span className="sub-text">ID: {row.medicine_id} | Unit: {row.unit}</span>
                    </td>

                    {/* Batch & Expiry */}
                    <td>
                      <span style={{ fontWeight: '600' }}>Batch: {row.batch_no || '-'}</span>
                      <span className="sub-text" style={{ color: theme.dangerDark }}>Exp: {row.expiry_date || '-'}</span>
                    </td>

                    {/* Issued Quantity */}
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: '800', color: theme.successDark, fontSize: '15px' }}>
                        {row.issued_qty}
                      </span>
                    </td>

                  </tr>
                ))}

                {/* INITIAL STATE */}
                {reportData.length === 0 && !hasSearched && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '60px 40px', color: theme.textMuted, fontSize: '14px', fontWeight: '600' }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>📅</div>
                      Please select a date range and click "Generate Data" to view the report.
                    </td>
                  </tr>
                )}

                {/* EMPTY STATE */}
                {reportData.length === 0 && hasSearched && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '60px 40px', color: theme.textMuted, fontSize: '14px', fontWeight: '600' }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>📂</div>
                      No stock issue records found for the selected dates.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default StockIssueReport;